import * as React from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input, softFocusRingClassName } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Soft focus ring shared by combobox triggers (matches Input/Textarea).
 * Prefer jarvis CSS vars when set by getJarvisControlStyle; fall back to --ring.
 */
export const comboboxTriggerFocusClassName = softFocusRingClassName;

/** Base trigger chrome — sm density (h-8) to match JarvisTextInput / JarvisSelect. */
export const comboboxTriggerClassName = cn(
  'h-8 w-full rounded-md border border-input bg-background px-2.5 text-sm font-normal text-foreground',
  'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
  comboboxTriggerFocusClassName
);

export type ComboboxOption = {
  value: string;
  label: React.ReactNode;
  /** Plain-text label used for search / display fallbacks. */
  searchLabel?: string;
  key?: string;
  disabled?: boolean;
};

function optionSearchLabel(option: ComboboxOption): string {
  if (option.searchLabel) return option.searchLabel;
  if (typeof option.label === 'string' || typeof option.label === 'number') {
    return String(option.label);
  }
  return option.value;
}

export type ComboboxProps = {
  options: ComboboxOption[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  /** When true, shows an in-popover search input and filters locally via cmdk. */
  searchable?: boolean;
  /**
   * Typeable field trigger (AsyncSelect). When false, uses a button trigger
   * that shows the selected label (JarvisSelect).
   */
  inputMode?: boolean;
  /** Controlled search text for the typeable trigger. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Disable cmdk client filtering (use when options are filtered externally). */
  shouldFilter?: boolean;
  id?: string;
  name?: string;
  /**
   * Form/a11y required flag only — Combobox never renders a visual asterisk.
   * Labels/asterisks belong on JarvisInputLabel or renderFieldLabel at the wrapper.
   */
  required?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  style?: React.CSSProperties;
  onOpenChange?: (open: boolean) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  'aria-invalid'?: boolean;
};

/**
 * shadcn Combobox pattern (Popover + Command).
 * Supports button or typeable-input triggers for Select / AsyncSelect wrappers.
 */
export function Combobox({
  options,
  value = null,
  onChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  disabled = false,
  loading = false,
  clearable = false,
  searchable = true,
  inputMode = false,
  searchValue,
  onSearchChange,
  shouldFilter = true,
  id,
  name,
  required,
  className,
  triggerClassName,
  contentClassName,
  style,
  onOpenChange,
  onFocus,
  onBlur,
  onKeyPress,
  'aria-invalid': ariaInvalid,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [internalSearch, setInternalSearch] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const selected = React.useMemo(
    () => options.find((opt) => String(opt.value) === String(value ?? '')),
    [options, value]
  );

  const displaySearch = searchValue !== undefined ? searchValue : internalSearch;

  const setSearch = (next: string) => {
    if (searchValue === undefined) setInternalSearch(next);
    onSearchChange?.(next);
  };

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    setOpen(next);
    onOpenChange?.(next);
    if (!next && !inputMode) {
      setInternalSearch('');
    }
  };

  const selectValue = (nextValue: string) => {
    const next = clearable && String(nextValue) === String(value ?? '') ? null : nextValue;
    onChange?.(next);
    if (inputMode) {
      const opt = options.find((o) => String(o.value) === String(nextValue));
      if (opt) setSearch(optionSearchLabel(opt));
    }
    handleOpenChange(false);
  };

  const clearValue = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange?.(null);
    setSearch('');
  };

  /** Resolve cmdk's data-value (option value) back to a selectable option. */
  const findOptionByCmdkValue = (cmdkValue: string | null | undefined) => {
    if (cmdkValue == null || cmdkValue === '') return undefined;
    return options.find((o) => String(o.value) === cmdkValue || optionSearchLabel(o) === cmdkValue);
  };

  const selectHighlightedOrFirst = () => {
    if (loading) return;
    const highlighted = listRef.current?.querySelector<HTMLElement>(
      '[cmdk-item][data-selected="true"], [cmdk-item][aria-selected="true"]'
    );
    const fromDom = findOptionByCmdkValue(highlighted?.getAttribute('data-value'));
    if (fromDom && !fromDom.disabled) {
      selectValue(String(fromDom.value));
      return;
    }
    const first = options.find((o) => !o.disabled);
    if (first) selectValue(String(first.value));
  };

  const selectedLabel = selected ? optionSearchLabel(selected) : '';
  const isEmpty = value == null || value === '';

  const list = (
    <Command shouldFilter={shouldFilter} className="bg-popover text-popover-foreground">
      {searchable && !inputMode ? (
        <CommandInput placeholder={searchPlaceholder} value={displaySearch} onValueChange={setSearch} className="h-9" />
      ) : null}
      <CommandList ref={listRef} className="max-h-60">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : (
          <>
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const text = optionSearchLabel(option);
                const optionValue = String(option.value);
                const isSelected = optionValue === String(value ?? '');
                return (
                  <CommandItem
                    key={option.key || `${optionValue}-${text}`}
                    // Use stable option value so onSelect maps 1:1; keywords keep label search.
                    value={optionValue}
                    keywords={[text]}
                    disabled={option.disabled}
                    onMouseDown={(e) => {
                      // Keep focus on the typeable trigger so Radix focus-outside
                      // does not close the popover before cmdk's click/onSelect.
                      if (inputMode) e.preventDefault();
                    }}
                    onSelect={() => selectValue(optionValue)}
                  >
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    <Check className={cn('ml-auto h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );

  // Popover width matches the field. Radix only defines
  // --radix-popover-trigger-width (there is no "anchor-width" var); it measures
  // the PopoverAnchor when one is set, so it covers inputMode's typeable field too.
  // An undefined var here left the list at width:auto — cut to "A…", "B…".
  const popoverWidthClass = 'w-[var(--radix-popover-trigger-width)]';

  return (
    <div className={cn('w-full', className)}>
      {/* Hidden field: form name binding + required for button-mode selects.
          Combobox never paints a required asterisk — labels own that. */}
      {name ? (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          required={!inputMode ? Boolean(required) && isEmpty : undefined}
          readOnly
          tabIndex={-1}
          aria-hidden
        />
      ) : !inputMode && required ? (
        <input type="hidden" value={value ?? ''} required={isEmpty} readOnly tabIndex={-1} aria-hidden />
      ) : null}
      {/* modal: nest focus/scroll lock so portaled options stay interactive inside Mantine Modal */}
      <Popover modal open={open} onOpenChange={handleOpenChange}>
        {inputMode ? (
          <PopoverAnchor asChild>
            <div className="relative w-full" data-field-control>
              <Input
                ref={inputRef}
                id={id}
                // Keep native required on the typeable control for form validation;
                // do not render any label/asterisk chrome here.
                required={required}
                disabled={disabled}
                placeholder={placeholder}
                value={displaySearch}
                aria-invalid={ariaInvalid}
                aria-required={required || undefined}
                aria-expanded={open}
                role="combobox"
                autoComplete="off"
                style={style}
                className={cn(comboboxTriggerClassName, 'pr-9', triggerClassName)}
                onChange={(e) => {
                  const next = e.currentTarget.value;
                  setSearch(next);
                  if (!open) handleOpenChange(true);
                }}
                onClick={() => !disabled && handleOpenChange(true)}
                onFocus={(e) => {
                  // Highlight the current label so the next keystroke replaces
                  // it. Without this, changing an already-filled field means
                  // deleting the text by hand — and users learned to leave one
                  // character behind, because emptying the box clears the
                  // selected value outright.
                  e.currentTarget.select();
                  onFocus?.();
                  if (!disabled) handleOpenChange(true);
                }}
                onBlur={() => {
                  // Delay so option click can register before close.
                  window.setTimeout(() => {
                    if (value && selectedLabel && displaySearch !== selectedLabel) {
                      setSearch(selectedLabel);
                    }
                    onBlur?.();
                  }, 120);
                }}
                onKeyPress={onKeyPress}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleOpenChange(false);
                    return;
                  }
                  // Focus stays on the typeable trigger (not cmdk), so Enter must
                  // select the highlighted/first option explicitly.
                  if (!open || loading) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    selectHighlightedOrFirst();
                  }
                }}
              />
              {clearable && displaySearch && !disabled ? (
                <button
                  type="button"
                  aria-label="Clear"
                  tabIndex={-1}
                  // The open popover is modal and turns pointer events off
                  // outside itself; this button has to opt back in.
                  className="pointer-events-auto absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm opacity-50 hover:opacity-100"
                  // Not a press outside the popover: keep it from the
                  // popover's dismiss listener, which would close the list.
                  onPointerDown={(e) => e.stopPropagation()}
                  // Keep focus in the input: blurring it would restore the
                  // selected label before the click lands.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setSearch('');
                    inputRef.current?.focus();
                    if (!open) handleOpenChange(true);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 shrink-0 opacity-50" />
              )}
            </div>
          </PopoverAnchor>
        ) : (
          <PopoverTrigger asChild>
            <Button
              type="button"
              id={id}
              variant="outline"
              role="combobox"
              data-field-control
              aria-expanded={open}
              aria-invalid={ariaInvalid}
              aria-required={required || undefined}
              disabled={disabled}
              style={style}
              className={cn(
                comboboxTriggerClassName,
                'justify-between',
                !selected && 'text-muted-foreground',
                triggerClassName
              )}
              onFocus={onFocus}
              onBlur={onBlur}
            >
              <span className="truncate text-left">{selected ? selected.label : placeholder}</span>
              <span className="ml-2 flex shrink-0 items-center gap-1">
                {clearable && value ? (
                  <X
                    className="h-4 w-4 opacity-50 hover:opacity-100"
                    onClick={clearValue}
                    aria-label="Clear selection"
                  />
                ) : null}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </span>
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent
          className={cn(popoverWidthClass, 'p-0', contentClassName)}
          align="start"
          onOpenAutoFocus={(e) => {
            // Keep focus on the typeable trigger for AsyncSelect UX.
            if (inputMode) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // After selection, do not yank focus away from the typeable trigger.
            if (inputMode) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            // Option rows are not focusable. mousedown blurs the input → focus
            // moves to body → Radix treats that as focus-outside and closes the
            // popover before cmdk's click/onSelect runs. Block that race in
            // inputMode; pointer-outside still dismisses normally.
            if (inputMode) e.preventDefault();
          }}
        >
          {list}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default Combobox;
